import { CloudRain } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

export const procenaDepresije: QuizConfig = {
  id: "procena-depresije",
  illustration: {
    src: "/images/quizzes/procena-depresije.webp",
    alt: "Muškarac sedi na ivici kreveta i teško započinje dan.",
  },
  lastModified: "2026-10-05",
  title: "Test za depresiju: kratka samoprocena",
  shortTitle: "Test za depresiju",
  description:
    "Besplatna samoprocena kroz 18 pitanja o raspoloženju, snu i energiji. Rezultat dobijaš odmah.",
  longDescription:
    "18 pitanja, 6-8 minuta. Kratka samoprocena o raspoloženju, snu, energiji i svakodnevnom funkcionisanju. Rezultat nije dijagnoza, ali može biti polazna tačka za dalje istraživanje.",
  icon: CloudRain,
  duration: "6-8 min",
  category: "Mentalno zdravlje",
  focusAreasWanted: [
    "depression",
    "emotional_regulation",
    "self_esteem",
    "shame_guilt",
  ],
  safety: {
    urgentOptionTexts: [
      "Razmišljam o smrti ili umiranju",
      "Imam konkretne misli o samopovređivanju",
    ],
  },
  questions: [
    {
      type: "radio",
      question: "Kako se osećaš većinu vremena?",
      options: [
        "Dobro, stabilno i pozitivno",
        "Ponekad tužno, ali nije previše često",
        "Često tužno i bezvoljno",
        "Skoro stalno osećam tugu i prazninu",
        "Stalno sam u stanju tuge i beznadežnosti",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Koliko često osećaš iscrpljenost bez fizičkog razloga?",
      options: [
        "Retko ili nikad",
        "Ponekad kada sam pod stresom",
        "Često, bez jasnog razloga",
        "Skoro svaki dan",
        "Stalno, nemam snage za ništa",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Da li si primetio/la promene u svom snu?",
      options: [
        "Ne, moj san je normalan",
        "Ponekad imam problem da zaspim ili se budim umoran/na",
        "Često teško zaspim ili se budim u toku noći",
        "Skoro uvek imam problema sa spavanjem",
        "Moj san je potpuno poremećen, ili previše spavam ili uopšte ne spavam",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question:
        "Kako depresija utiče na tvoj svakodnevni život? (označi sve što važi)",
      options: [
        "Gubim interesovanje za stvari koje su me nekada radovale",
        "Teško mi je da se motivišem za bilo šta",
        "Izolujem se od drugih i ne želim da pričam s ljudima",
        "Imam problema sa koncentracijom i donošenjem odluka",
        "Ništa od ovoga ne doživljavam",
      ],
      scores: [2, 2, 2, 2, 0],
    },
    {
      type: "radio",
      question: "Kako bi opisao/la svoje misli o budućnosti?",
      options: [
        "Optimistične i pozitivne",
        "Ponekad brinem, ali verujem da će biti bolje",
        "Često se osećam nesigurno i zabrinuto",
        "Vidim budućnost kao neizvesnu i mračnu",
        "Ne vidim nikakvu nadu za budućnost",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako se osećaš u društvu drugih ljudi?",
      options: [
        "Uživam u društvu i često se družim",
        "Ponekad mi prija, ali nekad želim da budem sam/a",
        "Često se osećam iscrpljeno nakon socijalnih interakcija",
        "Izbegavam druženja jer nemam energije",
        "Osećam se izolovano i ne želim kontakt sa drugima",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Koliko često osećaš krivicu ili da nisi dovoljno dobar/a?",
      options: [
        "Retko ili nikad",
        "Ponekad, ali ne previše često",
        "Često imam osećaj da ne vredim dovoljno",
        "Skoro svakodnevno osećam krivicu",
        "Stalno mislim da nisam dovoljno dobar/a ili da sam promašaj",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako opisuješ svoj apetit?",
      options: [
        "Normalan, nemam problema sa hranom",
        "Ponekad pojedem manje ili više nego obično",
        "Primetno jedem manje ili više nego ranije",
        "Gotovo uvek imam problem sa apetitom",
        "Izgubio/la sam interesovanje za hranu ili jedem previše kao način utehe",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako se budiš ujutru?",
      options: [
        "Odmarano/a i spremno za dan",
        "Ponekad umorno, ali ništa ozbiljno",
        "Većinom bez energije i volje",
        "Jedva ustajem iz kreveta, osećam težinu",
        "Ne želim da ustanem i suočim se sa danom",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako reaguješ kada neko pokušava da te uteši?",
      options: [
        "Osećam se bolje i podržano",
        "Cenim trud, ali mi često ne pomaže",
        "Nerviram se jer ne mislim da me razumeju",
        "Osećam se još gore jer ne želim da budem 'teret'",
        "Izbegavam ljude jer ne želim da pričam o svojim osećanjima",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Koliko često osećaš fizičke simptome povezane sa depresijom?",
      options: [
        "Nikada, nemam fizičke simptome",
        "Ponekad imam glavobolje ili napetost",
        "Često osećam bolove u telu bez jasnog razloga",
        "Skoro uvek se osećam fizički iscrpljeno",
        "Stalno osećam bol, umor i iscrpljenost",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Da li si razmišljao/la o traženju stručne pomoći?",
      options: [
        "Ne, ne mislim da mi je potrebna",
        "Ponekad razmišljam o tome, ali nisam siguran/na",
        "Često razmišljam, ali nisam preduzeo/la ništa",
        "Znam da mi je potrebna, ali se plašim da potražim pomoć",
        "Već sam potražio/la pomoć ili ozbiljno razmišljam o tome",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Koliko često imaš problema sa koncentracijom?",
      options: [
        "Nemam problema sa koncentracijom",
        "Ponekad mi je teško da se fokusiram",
        "Često mi je teško da pratim razgovore ili zadatke",
        "Skoro uvek imam problema da se koncentrišem na bilo šta",
        "Ne mogu da se fokusiram ni na najjednostavnije zadatke",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako se osećaš u vezi svojih postignuća i sposobnosti?",
      options: [
        "Zadovoljan/na sam svojim sposobnostima i postignućima",
        "Uglavnom sam zadovoljan/na, ali ponekad sumnjam u sebe",
        "Često mislim da nisam dovoljno sposoban/na",
        "Osećam da su moja postignuća bezvredna",
        "Ubeđen/a sam da ništa ne radim dobro i da sam potpuni promašaj",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Da li primećuješ promene u svom interesovanju za seksualne aktivnosti?",
      options: [
        "Ne, moj libido je normalan",
        "Ponekad primetim smanjeno interesovanje",
        "Često nemam želju za intimnošću",
        "Skoro potpuno sam izgubio/la interesovanje za seks",
        "Nemam apsolutno nikakvu želju za seksualnim aktivnostima",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question:
        "Koje od sledećih misli si imao/la u poslednje vreme? (označi sve što važi)",
      options: [
        "Osećam da sam teret drugima",
        "Mislim da bi drugima bilo bolje bez mene",
        "Razmišljam o smrti ili umiranju",
        "Imam konkretne misli o samopovređivanju",
        "Ništa od navedenog",
      ],
      scores: [2, 2, 3, 4, 0],
    },
    {
      type: "radio",
      question:
        "Koliko često osećaš da ne možeš da kontrolišeš važne aspekte svog života?",
      options: [
        "Retko, uglavnom imam kontrolu nad svojim životom",
        "Ponekad osećam da gubim kontrolu",
        "Često osećam da stvari izmaknu kontroli",
        "Većinu vremena osećam da nemam kontrolu nad svojim životom",
        "Potpuno sam izgubio/la osećaj kontrole nad svojim životom",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "textarea",
      question:
        "Opiši kako se depresija ili loše raspoloženje odražava na tvoj svakodnevni život:",
    },
  ],
  copy: {
    title: "Procena tvoje depresije",
    subtitle: "18 pitanja o raspoloženju, snu i energiji, sa rezultatom odmah.",
    scoreLabel: "Tvoj rezultat:",
    adviceTitle: "Saveti za tebe",
    resourcesTitle: "Korisni resursi",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
  landing: {
    summary:
      "Ovaj test pomaže da proceniš prisustvo i intenzitet simptoma depresije. Sa 18 pitanja o raspoloženju, energiji, snu i svakodnevnom funkcionisanju, dobijaš procenu koja može biti korisna polazna tačka. Test nije dijagnostički alat i ne zamenjuje razgovor sa stručnjakom.",
    highlights: [
      {
        title: "18 pitanja, 6-8 minuta",
        description:
          "Brza samoprocena koja pokriva ključne simptome depresije: raspoloženje, san, energiju i motivaciju.",
      },
      {
        title: "Poverljivo i besplatno",
        description:
          "Tvoji odgovori su privatni. Rezultate vidiš samo ti, bez registracije.",
      },
      {
        title: "Konkretni sledeći koraci",
        description:
          "Na osnovu rezultata dobijaš preporuke i resurse za dalju podršku.",
      },
    ],
    whoIsItFor:
      "Za svakoga ko primećuje promene u raspoloženju, motivaciji ili energiji i želi da proveri da li su simptomi u skladu sa depresijom. Test je posebno koristan ako se pitaš da li je ono što osećaš 'normalna tuga' ili nešto ozbiljnije.",
    whatYouLearn:
      "Saznaćeš koliko su tvoji simptomi izraženi i u kojim oblastima se najviše manifestuju: emocionalno stanje, telesni simptomi, socijalno funkcionisanje ili misaoni obrasci. Rezultat pomaže da prepoznaš obrasce koje možda nisi primetio/la.",
    howItWorks:
      "Test sadrži 18 pitanja o tvom raspoloženju, navikama i svakodnevnom funkcionisanju u poslednjih nedelja. Na osnovu odgovora, AI generiše personalizovanu procenu sa preporukama.",
    nextSteps:
      "Ako rezultati ukazuju na prisustvo simptoma depresije, preporučujemo razgovor sa licenciranim terapeutom. Na platformi možeš zakazati besplatnu uvodnu konsultaciju.",
    faq: [
      {
        question: "Kako prepoznati depresiju?",
        answer:
          "Depresija se manifestuje kroz trajnu tugu, gubitak interesovanja, promene u snu i apetitu, umor, teškoće sa koncentracijom i osećaj bezvrednosti. Ključna razlika od obične tuge je trajanje (više od dve nedelje) i uticaj na svakodnevno funkcionisanje.",
      },
      {
        question: "Da li je ovaj test za depresiju pouzdan?",
        answer:
          "Test je koristan kao alat za samoprocenu i može ukazati na prisustvo simptoma depresije. Međutim, on nije dijagnostički instrument. Samo licencirani stručnjak (psiholog ili psihijatar) može postaviti dijagnozu na osnovu kliničkog intervjua.",
      },
      {
        question: "Kada treba potražiti stručnu pomoć?",
        answer:
          "Ako simptomi traju duže od dve nedelje, ometaju svakodnevno funkcionisanje, ili imaš misli o samopovređivanju, važno je što pre potražiti pomoć. U hitnim situacijama, pozovi Centar za krizne situacije ili Hitnu pomoć.",
      },
      {
        question: "Koja je razlika između tuge i depresije?",
        answer:
          "Tuga je normalna emocija koja prolazi sa vremenom. Depresija je stanje koje traje nedeljama ili mesecima, utiče na sve aspekte života i često ne prolazi sama od sebe. Tuga obično ima jasan okidač, dok depresija može nastupiti bez očiglednog razloga.",
      },
      {
        question: "Da li ovaj test zamenjuje razgovor sa psihologom?",
        answer:
          "Ne. Ovaj test je polazna tačka, ne zamena za profesionalnu pomoć. Ako te rezultati zabrinjavaju, to je signal da razgovaraš sa licenciranim terapeutom koji može da napravi detaljniju procenu i predloži odgovarajući plan podrške.",
      },
    ],
    ctaLabel: "Započni test za depresiju",
  },
};
